import {FormProvider, SubmitHandler, useForm} from 'react-hook-form'
import {FC, FormEvent, useEffect, useMemo, useState} from 'react'
import {useTranslation} from 'next-i18next'
import {Box, Flex, HStack, Text} from '@chakra-ui/react'
import {parseCookies} from 'nookies'
import {useRouter} from 'next/router'
import {isArray, omit} from 'lodash-es'
import {Button, FavoriteButton} from '_app/components'
import toast from '_app/utils/toast'
import {ComplexTextValue, useGetCartLazyQuery} from '_app/graphql/generated/graphql'
import {AddProductsToCartDocument} from '_app/graphql/mutations/cart/AddProductToCart'
import {cart, cartIdVar, isCartOpenVar} from '_app/cache'
import useProductAvailability from '_app/hooks/useProductAvailability'
import {OptionType} from '_app/hooks/useProductOptions'
import {getCategory, getCurrencyCode, getDimension4, GTM_EVENTS,} from '_app/utils/dataLayer'
import {SelectedEngraveOption} from '_app/types/product'
import {EdroneEvents, getUrl, joinElements, parseUidsArray, uidToNumber} from '_app/utils/edrone'
import {ApplePay} from 'modules/applePay/ApplePay'
import {getDefaultSize, getNecklaceCheapestSize} from '../../utils/size'
import {getProductMainImage} from '../../utils/gallery'
import {getEngravingOptions} from '../../utils/getEngravingOptions'
import ProductNotification from '../ProductNotification'
import Size from './Size'
import useApplePay from './useApplePay'
import CartHelper from '../../../../helpers/CartHelper/CartHelper'
import {useLazyQuery, useMutation, useReactiveVar} from '@apollo/client'
import {CREATE_EMPTY_CART} from '../../../../_app/graphql/mutations/cart/CreateEmptyCart'
import RequestHelper from '../../../../helpers/RequestHelper/RequestHelper'
import {GetCartDocumentSmall} from '../../../../_app/graphql/queries/cart/GetCartDocumentSmall'
import {IS_CART_ACTIVE} from '../../../../_app/graphql/queries/cart/IsCartActive'
import ProductHelper from '../../../../helpers/ProductHelper/ProductHelper'
import Base64Helper from '../../../../helpers/Base64Helper/Base64Helper'
import {initializeApollo} from '../../../../helpers/ApolloHelper/ApolloHelper'
import getPrice from '../../../../_app/utils/localized/getPrice'

interface ProductFormProps {
    engraving?: boolean;
    sku: string;
    url_key: string;
    salable_qty: number;
    itemsOptions?: any;
    description?: ComplexTextValue;
    productId: string;
    requiredOptions?: {
        type: OptionType;
        options: { label: string; value: string }[];
    }[];
    productType: string;
    product: any;
    header: JSX.Element;
    children: any;
}

const ProductForm: FC<ProductFormProps> = ({
       children,
       requiredOptions,
       salable_qty,
       itemsOptions,
       productId,
       productType,
       product,
       header,
       ...item
   }) => {
    const {onApplePayClick, onModalClose} = useApplePay()
    const methods = useForm()
    const cartId = useReactiveVar(cartIdVar) || parseCookies().cartId
    const router = useRouter()
    const {t} = useTranslation('product')
    const {locale} = router
    const apolloClient = initializeApollo(null, {locale})
    const showApplePayButton = process.env.NEXT_PUBLIC_APPLE_PAY_ENABLED === '1' && typeof window !== 'undefined' && window.ApplePaySession?.canMakePayments()
    const [isCartActive]: any = useLazyQuery(IS_CART_ACTIVE)
    const [formSelectedOptions, setFormSelectedOptions] = useState<string[]>([])
    const {
        handleSubmit,
        formState: {isSubmitting},
    } = methods
    const cheapestSizeOption = getNecklaceCheapestSize(product)
    const selectedOptions = methods
        .watch('selected_options')
        ?.flatMap((item: any) => item)

    const {availableProductAmount} = useProductAvailability({
        salable_qty,
        selectedOptions,
        itemsOptions,
        product,
    })
    const defaultOptionValues = useMemo(
        () =>
            requiredOptions?.map(option => {
                const {type, options} = option || {}

                return type === 'size'
                    ? getDefaultSize(options, cheapestSizeOption)
                    : options?.[0]?.value
            }),
        [requiredOptions, cheapestSizeOption]
    )
    const edronePayload = {
        productId: uidToNumber(product.uid),
        productSku: product.sku as string,
        productUrl: getUrl(product.url_key),
        productTitle: product.name as string,
        productImages: getProductMainImage(product),
        productCategoryIds: parseUidsArray(
            product?.categories?.map(category => category?.uid) || []
        ),
        productCategoryNames: joinElements(
            product.categories?.map(category => category?.name) || []
        ),
        productAvailability: product.salable_qty ?? 1,
    }
    useEffect(() => {
        const waitConsent = setTimeout(() => {
            GTM_EVENTS.productDetailView({
                ecommerce: {
                    currencyCode: getCurrencyCode(product),
                    detail: {
                        products: [
                            {
                                id: product?.sku as string,
                                name: product?.name as string,
                                price: product?.price_range?.minimum_price?.final_price
                                    ?.value as number,
                                category1: getCategory(product),
                                category2: 'Nieokreślone',
                                brand: 'Lilou',
                                dimension6: ProductHelper.getNameAvailable(ProductHelper.getAvailableAmount(product?.items, undefined, product?.salable_qty, product)),
                                dimension4: getDimension4(product?.categories),
                                salable_qty,
                                regular_price: product?.price_range?.minimum_price?.regular_price?.value,
                                omnibus_price: product?.omnibus_price,
                                item_markdown: product?.price_range?.minimum_price?.regular_price?.value && product?.price_range?.minimum_price?.final_price?.value !== null && product?.price_range?.minimum_price?.final_price?.value !== undefined && product?.price_range?.minimum_price?.regular_price?.value > product?.price_range?.minimum_price?.final_price?.value ? 'yes' : 'no',
                                availableProductAmount,
                            },
                        ],
                    },
                },
            })
            EdroneEvents.productView(edronePayload, locale)
        }, 1000)
        return () => clearTimeout(waitConsent)
    }, [router.asPath])
    const [getCartData]: any = useGetCartLazyQuery()
    useEffect(() => {
        if (defaultOptionValues) {
            defaultOptionValues.forEach((value, index) => methods.setValue(`selected_options[${index}]`, [value]))
        }
    }, [methods, defaultOptionValues])
    const customizableDropDownOption: any = useMemo(() => {
        return product.options
            ?.find((option: any) => option?.__typename === 'CustomizableDropDownOption')
            ?.drop_down_option?.find(option => option?.sku === null)?.uid
    }, [product.options])
    useEffect(() => {
        if (selectedOptions?.length > 0) {
            setFormSelectedOptions(prevOptions => {
                if (JSON.stringify(prevOptions) !== JSON.stringify(selectedOptions)) {
                    return [...selectedOptions] // Upewnij się, że tworzysz nową kopię tablicy
                }
                return prevOptions
            })
        }
    }, [selectedOptions])
    const onSubmit: SubmitHandler<any> = async engraving => {
        const {
            selected_options,
            engrave_options,
            selected_engrave_options = {},
            entered_options = [],
            ...data
        } = engraving
        const selectedEngraveOptionsArray = getEngravingOptions(selected_engrave_options)
        const formatOptionValue = async (option?: SelectedEngraveOption) => {
            if (!option) return ''
            const {value: inputValue, file, options} = option
            let value: any = ''
            if (inputValue && (typeof inputValue === 'string' || typeof inputValue === 'number')) {
                value = inputValue.toString()
            }
            if (inputValue && typeof inputValue === 'object') {
                value = inputValue?.value?.toString()
            }
            if (file) {
                value = await Base64Helper.getFromURL(file)
                if (isArray(file)) {
                    value = await Base64Helper.getFromFile(file[0])
                }
            }
            if (options?.color) value += `|${options.color}`
            if (options?.font) value += `|${options.font}`
            if (typeof inputValue === 'object' && inputValue?.options?.font)
                value += `|${inputValue?.options.font}`
            return value
        }
        const getEnteredOptions = async () => {
            return Promise.all(
                selectedEngraveOptionsArray.map(async option => ({
                    uid: option?.uid,
                    value: await formatOptionValue(option),
                }))
            )
        }
        const enteredEngraveOptions = await getEnteredOptions()
        let res: any = null
        if (cartId) {
            res = await isCartActive({
                variables: {
                    masked_cart_id: cartId,
                },
            })
        }
        const isActive = cartId ? await res?.data?.isCartActive?.is_active : false
        const cartIdTemp: string = await CartHelper.createEmptyCart(apolloClient, cartId, isActive)
        const variables = {
            cartId: cartIdTemp,
            cartItems: [
                {
                    sku: item.sku,
                    selected_options: selected_options?.flatMap(x => x) ?? [],
                    entered_options: [...entered_options, ...enteredEngraveOptions],
                    quantity: 1,
                    ...data,
                },
            ],
        }
        return apolloClient.mutate({
            mutation: AddProductsToCartDocument,
            variables,
        }).then(async (res) => {
            const response = RequestHelper.onComplete(res, 'addProductsToCart', locale)
            if (response) {
                await cart(res?.data?.addProductsToCart)
                GTM_EVENTS.addToCart({
                    type: product?.__typename === 'SimpleProduct' ? 'standard_product' : product?.__typename === 'BundleProduct' ? ProductHelper.isPersonalizedProduct(product) ? 'personalized_product' : 'configured_products' : '',
                    origin: 'product page',
                    ecommerce: {
                        currencyCode: getCurrencyCode(item),
                        add: {
                            products: [
                                {
                                    id: product?.sku,
                                    name: product?.name,
                                    quantity: 1,
                                    price: product?.price_range?.minimum_price?.final_price?.value,
                                    dimension4: getDimension4(product?.categories),
                                    category1: product.main_category?.[0]?.name,
                                    category2: 'Nieokreślone',
                                    brand: 'Lilou',
                                    dimension8: ProductHelper.isPersonalizedProduct(product) ? 'Personalizowany' : 'Standard',
                                },
                            ],
                        },
                    },
                })
                const edroneAddToCartPayload = omit(edronePayload, 'productAvailability')
                EdroneEvents.addToCart(edroneAddToCartPayload, locale)
                isCartOpenVar(true)
            }
        }).catch((error) => RequestHelper.internalServerError(error))
    }
    const handleAddToCartClick = async (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault()
        if (product?.price_range?.minimum_price?.final_price?.value !== 0) {
            onModalClose()
            await handleSubmit(onSubmit)()
        } else {
            toast({description: t('NOT_POSSIBLE_TO_BUY'), status: 'error'})
        }
    }
    const handleApplePayButtonClick = async () => {
        const cartData = await getCartData({variables: {cartId}})
        const isTheSameInCart = !!cartData?.data?.cart?.items?.find(cartItem => {
            return cartItem?.product?.url_key === item?.url_key
        })
        if (!isTheSameInCart) onApplePayClick(onSubmit)
    }
    const generateButton = () => (
        <Flex flexDirection="column">
            <HStack>
               <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                   {
                       availableProductAmount && availableProductAmount < 10 && (
                           <Box
                               textAlign="center"
                               py={4}
                               border="solid 1px #d6d6d6"
                               background="#fafafa"
                               mb={3}
                               fontWeight={300}
                           >
                               <Text as="span">{t('BUY_BEFORE_IT_DISAPPEARS')}</Text>
                           </Box>
                       )
                   }
                   <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: '10px' }}>
                       <Button width="full" type="submit" isLoading={isSubmitting}>
                           {t('ADD_TO_CART', {ns: 'common'})}
                       </Button>
                       <FavoriteButton
                           sku={item.sku}
                           border="1px solid black"
                           minHeight="3.125rem"
                           minWidth="3.125rem"
                           isGold
                       />
                   </Box>
               </Box>
            </HStack>
            {showApplePayButton && (
                <ApplePay
                    displayButtonInColumn
                    buttonClickCallback={handleApplePayButtonClick}
                    modalCloseCallback={onModalClose}
                />
            )}
        </Flex>
    )
    return (
        <>
            <FormProvider {...methods}>
                <form id="product-form" onSubmit={handleAddToCartClick}>
                    {header}
                    {defaultOptionValues?.map(
                        (option, index) =>
                            requiredOptions?.[index]?.type && (
                                <Size
                                    name={`selected_options[${index}]`}
                                    key={`selected_options-${index}`}
                                    sizes={requiredOptions?.[index].options ?? []}
                                    optionType={requiredOptions?.[index].type}
                                    productType={productType}
                                />
                            )
                    )}
                    {children}
                    {availableProductAmount && availableProductAmount > 0 ? generateButton() : ''}
                </form>
            </FormProvider>
            {
                (typeof availableProductAmount === 'number' && availableProductAmount < 1) && (
                    <ProductNotification {...{productId}}>
                        <FavoriteButton
                            sku={item.sku}
                            border="1px solid black"
                            minHeight="3.125rem"
                            minWidth="3.125rem"
                            isGold
                        />
                    </ProductNotification>
                )}
        </>
    )
}

export default ProductForm
